/**
 * What an asset is worth (client notes 11/2, "Proposed Asset Value Calculation"):
 *
 *   Asset Value = Type (1 to 65) + Crown Jewel (0 or 20) + Position (1 to 15)
 *
 * so it runs up to 100. The notes give two figures for the Type, "1-70" in one paragraph and "1-65" in the calculation;
 * the calculation is followed because with a Crown Jewel of 20 and a Position of up to 15 it adds up to exactly 100.
 * The notes name the kinds of asset (compute, storage, endpoint, IAM, network) but give no value for each, so the table
 * below is the console's default, kept in one place so the client can change a number without touching a screen. The
 * Crown Jewel is the tag read by `lib/crownJewel.ts`; the Position is a tag too (`public`, `production`, `internal`, `qa`,
 * `non-production`, `development`), and when an asset has several the highest counts. An asset with no Position tag counts
 * the lowest, 1, and says so. Nothing here is a measurement: it is how much the asset matters, as decided by its type and tags.
 */
import { isCrownJewel } from './crownJewel';

export const TYPE_VALUE_MAX = 65;
export const CROWN_JEWEL_VALUE = 20;
export const POSITION_VALUE_MAX = 15;
export const ASSET_VALUE_MAX = TYPE_VALUE_MAX + CROWN_JEWEL_VALUE + POSITION_VALUE_MAX;

/** The kinds of asset, most important first, with what words in the record's type tell one kind from another. */
const ASSET_TYPES: { label: string; value: number; words: RegExp }[] = [
  { label: 'Identity and access', value: 65, words: /(iam|identity|role|user|account|group|policy|credential|secret|key|directory|sso|principal)/ },
  { label: 'Data store', value: 60, words: /(database|\bdb\b|rds|storage|bucket|s3|data|lake|warehouse|vault|backup|snapshot)/ },
  { label: 'Business service', value: 55, words: /(business|service|application|\bapp\b|\bapi\b|platform)/ },
  { label: 'Compute', value: 40, words: /(compute|ec2|vm|instance|container|k8s|kubernetes|cluster|lambda|function|server|host|node)/ },
  { label: 'Network', value: 35, words: /(network|vpc|subnet|firewall|gateway|balancer|dns|route|vpn)/ },
  { label: 'Endpoint', value: 25, words: /(endpoint|laptop|workstation|device|mobile|desktop|fleet)/ },
];
const OTHER_TYPE = { label: 'Other', value: 15 };

/** Where an asset sits, most exposed first. */
export const POSITIONS: { tag: string; label: string; value: number }[] = [
  { tag: 'public', label: 'Public', value: 15 },
  { tag: 'production', label: 'Production', value: 12 },
  { tag: 'internal', label: 'Internal', value: 8 },
  { tag: 'qa', label: 'QA', value: 4 },
  { tag: 'non_production', label: 'Non-production', value: 2 },
  { tag: 'development', label: 'Development', value: 1 },
];
const POSITION_ALIASES: Record<string, string> = {
  prod: 'production',
  dev: 'development',
  test: 'qa',
  staging: 'non_production',
  nonprod: 'non_production',
  external: 'public',
};
const UNTAGGED_POSITION = 1;

/** What the console knows of an asset: the graph's node, or a record. */
export interface AssetLike {
  tags?: unknown;
  universalType?: string;
  universalDomain?: string;
  providerType?: string;
  provider?: string;
  label?: string;
}

export interface AssetValue {
  /** Up to 100; with the default table the least is 16. */
  total: number;
  type: number;
  typeLabel: string;
  crownJewel: boolean;
  position: number;
  /** The Position tag it was read from; null when the asset has none. */
  positionLabel: string | null;
}

function normalizeTag(tag: unknown): string {
  const word = String(tag ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return POSITION_ALIASES[word] ?? word;
}

function positionOf(tags: unknown): { value: number; label: string | null } {
  if (!Array.isArray(tags)) return { value: UNTAGGED_POSITION, label: null };
  const wanted = new Set(tags.map(normalizeTag));
  const hit = POSITIONS.find((position) => wanted.has(position.tag));
  return hit ? { value: hit.value, label: hit.label } : { value: UNTAGGED_POSITION, label: null };
}

function typeOf(asset: AssetLike): { value: number; label: string } {
  const text = [asset.universalType, asset.providerType, asset.universalDomain].join(' ').toLowerCase();
  const hit = ASSET_TYPES.find((kind) => kind.words.test(text));
  return hit ?? OTHER_TYPE;
}

export function assetValueOf(asset: AssetLike): AssetValue {
  const type = typeOf(asset);
  const crownJewel = isCrownJewel(asset);
  const position = positionOf(asset.tags);
  return {
    total: type.value + (crownJewel ? CROWN_JEWEL_VALUE : 0) + position.value,
    type: type.value,
    typeLabel: type.label,
    crownJewel,
    position: position.value,
    positionLabel: position.label,
  };
}

/** The sum written out, for a tooltip: "Type 65 (Identity and access) + Crown Jewel 20 + Position 12 (Production)". */
export function describeAssetValue(value: AssetValue): string {
  return [
    `Type ${value.type} (${value.typeLabel})`,
    `Crown Jewel ${value.crownJewel ? CROWN_JEWEL_VALUE : 0}`,
    `Position ${value.position} (${value.positionLabel ?? 'not tagged'})`,
  ].join(' + ');
}
